import { Modal, ModalCloseButton, ModalContent, ModalOverlay } from '@chakra-ui/react';
import { useSnackbar } from '@components/snackbar';
import Tippy from '@tippyjs/react';
import { useRouter } from 'next/router';
import * as React from 'react';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { isFreeUser } from 'src/constants/plans';
import { useBoardStore } from 'src/features/boards';
import { TaskEdit } from 'src/features/tasks';
import { useTasksStore } from 'src/features/tasks/store/useTasksStore';
import { useAuth } from 'src/shared/hooks/use-auth';
import { shallow } from 'zustand/shallow';

import { useKanbanStore } from '../../store';
import { KanbanState } from '../../store/types';
import { KanbanColumn } from '../Column';

const { useEffect, useState } = React;

export const Board = () => {
  const {
    columns,
    onAddNewColumn,
    currentSelectedItem,
    onCloseCurrentSelectedItem,
    onUpdateItemPlacement,
    isReadOnly,
    onChangeColumnOrder,
    setItem,
    onDeleteItem,
    onDuplicateItem,
    onChangeCurrentSelectedItem,
  } = useKanbanStore(boardSelector, shallow);
  const { access, isPublicView, actions, clearSelectedItems } = useBoardStore((s) => ({
    access: s.access,
    isPublicView: s.isPublicView,
    actions: s.actions,
    clearSelectedItems: s.clearSelectedItems,
  }));
  const onDeleteSubtask = useTasksStore((s) => s.onDeleteSubTask);
  const [openSnackbar] = useSnackbar();
  const [isDeletePromptOpen, setIsDeletePromptOpen] = useState<boolean>(false);
  const [isDuplicatePromptOpen, setIsDuplicatePromptOpen] = useState<boolean>(false);

  const router = useRouter();
  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;
  const authData = useAuth((s) => s.userDetails);

  const handleTaskViewClose = () => {
    let link = `/${mode}/${orgId}/files/${router.query.slug}`;
    //public
    if (isPublicView) {
      link = link + `/public`;
    }

    router.push({
      pathname: link,
      query: {
        view: router.query.view,
        type: router.query.type,
        viewId: router.query.viewId,
      },
    });
  };

  useEffect(() => {
    if (!router?.query?.taskId) {
      onCloseCurrentSelectedItem();
    }
  }, [router]);

  useEffect(() => {
    if (router?.query?.taskId) {
      const allTasks = columns.reduce((acc, column) => {
        const subtasks = column.items.reduce((acc, item) => {
          return [...acc, ...(item.subtasks || [])];
        }, []);
        return [...acc, ...column.items, ...subtasks];
      }, []);
      const foundTask = allTasks.find((item) => item.id === router.query.taskId);
      if (foundTask) {
        onChangeCurrentSelectedItem({
          isOpen: true,
          columnId: foundTask.column,
          mode: 'edit',
          item: foundTask,
        });
      }
    }
  }, [router?.query?.taskId]);

  useEffect(() => {
    return () => {
      clearSelectedItems();
    };
  }, []);

  const onDropItem = (result) => {
    const { destination, source, draggableId, type } = result;

    if (!destination) {
      return;
    }

    if (type === 'column') {
      const columnIds = columns?.map((column) => column.id);
      columnIds.splice(source.index, 1);
      columnIds.splice(destination.index, 0, draggableId);
      onChangeColumnOrder({ columnIds });
      return;
    }

    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    const response: any = onUpdateItemPlacement({
      destColId: destination.droppableId,
      itemId: draggableId,
      sourceColId: source.droppableId,
      sourceIndex: source.index,
      destIndex: destination.index,
      fetchTimer: actions.fetchTimer,
    });

    //TODO:Error to be handled in Mytasks as well
    if (!response) {
      openSnackbar({
        text: 'An error has occured',
        snackType: 'error',
        position: 'bottom-center',
      });
      const boardId = router.query.view as string;
      actions.fetchBoard(boardId);
    }
  };

  const onToggleDeletePrompt =
    (isOpen = true) =>
    () => {
      setIsDeletePromptOpen(isOpen);
    };

  const onToggleDuplicatePrompt =
    (isOpen = true) =>
    () => {
      setIsDuplicatePromptOpen(isOpen);
    };

  const handleDeleteTask = () => {
    if (currentSelectedItem.item?.id && currentSelectedItem.columnId) {
      if (currentSelectedItem.item.isSubtask) {
        onDeleteSubtask({
          parentTaskId: currentSelectedItem.item.parentId as string,
          taskId: currentSelectedItem.item.id as string,
        });
      } else {
        onDeleteItem({
          columnId: currentSelectedItem.columnId,
          itemId: currentSelectedItem.item.id,
        });
      }
      setIsDeletePromptOpen(false);
      router.push(
        `/${mode}/${orgId}/files/${router.query.slug}?view=${router.query.view}&type=${router.query.type}`
      );
    } else {
      const allTasks = columns.reduce((acc, column) => {
        const subtasks = column.items.reduce((acc, item) => {
          return [...acc, ...(item.subtasks || [])];
        }, []);
        return [...acc, ...column.items, ...subtasks];
      }, []);

      const foundTask = allTasks.find((item) => item.id === router.query.taskId);
      if (foundTask) {
        if (foundTask.isSubtask) {
          onDeleteSubtask({
            parentTaskId: foundTask.parentId as string,
            taskId: foundTask.id as string,
          });
        } else {
          onDeleteItem({
            columnId: foundTask.column as string,
            itemId: router.query.taskId as string,
          });
        }
        setIsDeletePromptOpen(false);
        router.push(
          `/${mode}/${orgId}/files/${router.query.slug}?view=${router.query.view}&type=${router.query.type}`
        );
      }
    }
  };

  const handleDuplicateTask = () => {
    if (currentSelectedItem.item?.id && currentSelectedItem.columnId) {
      onDuplicateItem({
        columnId: currentSelectedItem.columnId,
        itemId: currentSelectedItem.item.id,
      });
      setIsDuplicatePromptOpen(false);
      router.push(
        `/${mode}/${orgId}/files/${router.query.slug}?view=${router.query.view}&type=${router.query.type}`
      );
    }
  };

  const onOpenMoveTask = () => {
    if (currentSelectedItem.item && currentSelectedItem.columnId) {
      onChangeCurrentSelectedItem({
        item: currentSelectedItem.item,
        isOpen: true,
        mode: 'move',
        columnId: currentSelectedItem.columnId,
        itemIndex: currentSelectedItem.itemIndex,
      });
    }
  };

  const handleAddColumn = async () => {
    const response: any = await onAddNewColumn();
    if (response) handleScrollToEnd();
  };

  const handleScrollToEnd = () => {
    const boardContainer = document?.getElementById('board-container');
    if (boardContainer) {
      boardContainer.focus();
      boardContainer.scrollLeft = boardContainer?.scrollWidth - boardContainer.clientWidth;
    }
  };

  return (
    <DragDropContext onDragEnd={onDropItem}>
      <Droppable droppableId="all-columns" direction="horizontal" type="column">
        {(provided) => (
          <div className="bg-gray-100">
            <section
              id="board-package-wrapper"
              className="mx-auto py-16 pl-30 mobile:px-16 desktop:px-24 bigScreen:px-64 pt-6 bg-gray-100"
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              <div id="board-container" className="overflow-x-scroll no-scrollbar mb-8 flex">
                <div className="flex">
                  {columns?.map((column, index) => (
                    <KanbanColumn key={column.id} column={column} index={index} />
                  ))}
                </div>
                {provided.placeholder}
                {router?.query?.type !== 'agile' && (
                  <div>
                    {!isReadOnly &&
                    access !== 'view' &&
                    authData?.plan?.type &&
                    !isFreeUser(authData?.plan?.type) ? (
                      <Tippy content={<span>Create new column</span>}>
                        <button
                          className=" text-gray-500 mt-18 block w-96"
                          tabIndex={-1}
                          onClick={handleAddColumn}
                        >
                          Add Column
                        </button>
                      </Tippy>
                    ) : authData?.plan?.type && isFreeUser(authData?.plan?.type) ? (
                      <Tippy content={<span>Upgrade to unlock</span>}>
                        <button className=" text-gray-500 mt-18 block w-96" tabIndex={-1}>
                          Add Column
                        </button>
                      </Tippy>
                    ) : (
                      <></>
                    )}
                  </div>
                )}
              </div>
            </section>
          </div>
        )}
      </Droppable>
      <Modal
        isOpen={isDeletePromptOpen}
        onClose={() => {
          setIsDeletePromptOpen(false);
        }}
      >
        <ModalOverlay />
        <ModalContent
          style={{
            maxWidth: '46rem',
            padding: '32px',
            marginTop: '20rem',
            borderRadius: '8px',
          }}
        >
          <ModalCloseButton />
          <p className="mb-32 text-md text-gray-999">Are you sure you wish to proceed?</p>
          <div className="flex justify-end">
            <button
              onClick={() => {
                setIsDeletePromptOpen(false);
              }}
              className="border-blue-500 text-blue-500 border rounded-sm px-24 py-13 mr-8"
            >
              Cancel
            </button>
            <button
              className="border-red-700 text-red-700 border rounded-sm px-24 py-13"
              onClick={handleDeleteTask}
            >
              Delete
            </button>
          </div>
        </ModalContent>
      </Modal>
      <Modal
        isOpen={isDuplicatePromptOpen}
        onClose={() => {
          setIsDuplicatePromptOpen(false);
        }}
      >
        <ModalOverlay />
        <ModalContent
          style={{
            maxWidth: '46rem',
            padding: '32px',
            marginTop: '20rem',
            borderRadius: '8px',
          }}
        >
          <ModalCloseButton />
          <p className="mb-32 text-md text-gray-999">
            Are you sure you wish to duplicate this task?
          </p>
          <div className="flex justify-end">
            <button
              onClick={() => {
                setIsDuplicatePromptOpen(false);
              }}
              className="border-blue-500 text-blue-500 border rounded-sm px-24 py-13 mr-8"
            >
              Cancel
            </button>
            <button
              className="border-red-700 text-red-700 border rounded-sm px-24 py-13"
              onClick={handleDuplicateTask}
            >
              Duplicate
            </button>
          </div>
        </ModalContent>
      </Modal>
      <Modal isOpen={!!router.query.taskId} onClose={handleTaskViewClose} motionPreset="none">
        <ModalOverlay />
        <ModalContent
          style={{
            position: 'initial',
            maxWidth: '118rem',
            maxHeight: '74rem',
          }}
          className={`min-w-768 h-vh90 bg-white rounded-lg static`}
        >
          {router.query.taskId && (
            <TaskEdit
              id={router?.query?.taskId as string}
              onDelete={onToggleDeletePrompt(true)}
              onDuplicate={onToggleDuplicatePrompt(true)}
              onMove={onOpenMoveTask}
              onChangeTask={(e) => {
                if (!e.task.isSubtask && e.task.column && e.task.id) {
                  setItem({
                    columnId: e.task.column,
                    item: e.task as any,
                    itemId: e.task.id,
                  });
                }
              }}
            />
          )}
        </ModalContent>
      </Modal>

      {currentSelectedItem.mode !== 'add' && (
        <Modal
          isOpen={(currentSelectedItem.isOpen && currentSelectedItem.mode !== 'edit') || false}
          onClose={handleTaskViewClose}
          motionPreset="none"
        >
          <ModalOverlay />
          <ModalContent
            style={{
              position: 'initial',
              maxWidth: currentSelectedItem.mode === 'move' ? '80rem' : '102rem',
              // maxHeight: currentSelectedItem.mode === 'add' ? '69.5rem' : '',
            }}
            className={`${
              currentSelectedItem.mode === 'move' ? 'min-w-500' : 'min-w-768 min-h-vh70'
            } bg-white rounded-lg static`}
          ></ModalContent>
        </Modal>
      )}
    </DragDropContext>
  );
};
const boardSelector = (s: KanbanState) => ({
  columns: s.columns,
  onAddNewColumn: s.onAddNewColumn,
  currentSelectedItem: s.currentSelectedItem,
  onCloseCurrentSelectedItem: s.onCloseCurrentSelectedItem,
  isReadOnly: s.isReadOnly,
  onUpdateItemPlacement: s.onUpdateItemPlacement,
  onChangeColumnOrder: s.onChangeColumnOrder,
  setItem: s.setItem,
  onDeleteItem: s.onDeleteItem,
  onDuplicateItem: s.onDuplicateItem,
  onChangeCurrentSelectedItem: s.onChangeCurrentSelectedItem,
});
